import { useCallback, useEffect, useState } from 'react'
import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import usePlacesAutocomplete, { getGeocode } from 'use-places-autocomplete'

import AutocompleteInput, { AutocompleteValue } from '@/components/ui/inputs/AutocompleteInput'
import { useLanguage } from '@/hooks/useLanguage'
import { googlePlacesLanguage, googlePlacesRegion, ParsedGoogleAddress, parseGoogleAddress } from '@/utils/google-address'

export type AddressAutocompleteInputProps = {
  disabled?: boolean
  fullWidth?: boolean
  label?: string
  placeholder?: string
  /**
   * Resolve the country to its English display name instead of the localized one.
   * Required wherever Country is a dropdown of English labels (SEHA).
   */
  canonicalCountry?: boolean
  /** Called with the structured address once a suggestion is picked. */
  onSelect: (address: ParsedGoogleAddress) => void
}

function isPlacesReady(): boolean {
  return typeof window !== 'undefined' && !!window.google?.maps?.places
}

/**
 * Search field that fills a structured address from Google Places.
 *
 * It holds no form state of its own — picking a suggestion hands the parsed
 * address to `onSelect`, which writes the individual Street / City / Postal code
 * / Country fields. Those stay editable so a user can always correct or type an
 * address Google does not know.
 *
 * Suggestions and the resolved address are requested in the active app language,
 * so the field works on every locale rather than only the browser default.
 */
export function AddressAutocompleteInput(props: AddressAutocompleteInputProps) {
  const { disabled = false, fullWidth = true, label, placeholder, canonicalCountry = false, onSelect } = props
  useLingui()
  const language = useLanguage()

  const placesLanguage = googlePlacesLanguage(language)
  const region = googlePlacesRegion(language)

  const {
    ready,
    suggestions: { data, loading },
    setValue: setSearchValue,
    clearSuggestions,
    init,
  } = usePlacesAutocomplete({
    debounce: 300,
    // The Maps script is loaded asynchronously at bootstrap, so it is usually not
    // there yet on first mount. Initialise once it lands instead of on mount.
    initOnMount: false,
    requestOptions: { language: placesLanguage, ...(region ? { region } : {}) },
  })

  const [inputValue, setInputValue] = useState('')
  const [resolving, setResolving] = useState(false)

  useEffect(() => {
    if (ready) return
    if (isPlacesReady()) {
      init()
      return
    }
    const timer = window.setInterval(() => {
      if (!isPlacesReady()) return
      window.clearInterval(timer)
      init()
    }, 300)
    return () => window.clearInterval(timer)
  }, [ready, init])

  const handleChange = useCallback(
    async (newValue: AutocompleteValue) => {
      if (!newValue || Array.isArray(newValue)) return

      setResolving(true)
      try {
        const [result] = await getGeocode({ placeId: newValue.value, language: placesLanguage })
        if (result) onSelect(parseGoogleAddress(result, { canonicalCountry }))
      } catch {
        // A failed lookup must not block the form: the fields below stay editable,
        // so the user can simply type the address instead.
      } finally {
        setResolving(false)
        // The picked address now lives in the individual fields; keep the search
        // box empty so it reads as a search box and not as a stale value.
        setInputValue('')
        setSearchValue('', false)
        clearSuggestions()
      }
    },
    [canonicalCountry, clearSuggestions, onSelect, placesLanguage, setSearchValue]
  )

  return (
    <AutocompleteInput
      disabled={disabled || !ready}
      freeSolo={false}
      fullWidth={fullWidth}
      inputValue={inputValue}
      label={label ?? t`Search an address`}
      loading={loading || resolving}
      name="address-autocomplete"
      options={data.map((suggestion) => ({ label: suggestion.description, value: suggestion.place_id }))}
      placeholder={placeholder}
      value={null}
      onChange={handleChange}
      onInputChange={(newInputValue: string) => {
        setInputValue(newInputValue)
        setSearchValue(newInputValue)
      }}
    />
  )
}

export default AddressAutocompleteInput
